/**
 * HomeMenu.jsx — Unified floating menu for the v2 desktop.
 *
 * Replaces the previous single-purpose AddModuleLauncher. Same FAB
 * (bottom-right "+") opens a tabbed panel with five sections:
 *
 *   • Widgets — categorised, searchable widget catalog (drop-in of
 *     the old launcher grid).
 *   • Theme   — pick one of the 4 themes; persists to localStorage.
 *   • Settings— per-user prefs (UI tweaks). Reads/writes /me/prefs.
 *   • Account — Discord / ESO / Fissal Relay link status + logout.
 *   • Layout  — reset, restore-all, save-as-spy-template (admin).
 *
 * Anonymous users see only the Widgets tab plus a login CTA.
 *
 * The class names are kept under `.v2-launcher__*` for visual
 * continuity with the previous launcher (those styles stay in
 * AddModuleLauncher.css; new tab structure uses `.v2-home__*`).
 */
import { createSignal, createMemo, createResource, createEffect, For, Show, onMount, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import wm from '../lib/wmStore.js';
import { homeMenu } from '../lib/homeMenuStore.js';
import api from '../lib/apiClient.js';
import { toast } from '../lib/toastStore.js';
import { applyTheme as setTheme, THEMES, currentTheme,
  CUSTOM_THEME_FIELDS,
  applyThemeOverride, currentThemeOverride, resetThemeOverride, storedThemeOverride,
  previewThemeOverride, clearThemeOverridePreview,
  applyGlowPercent, currentGlowPercent,
  applyNeonPercent, currentNeonPercent,
  applyWmBorderWidth, currentWmBorderWidth,
  applyFontScalePercent, currentFontScalePercent,
  applyDashboardImage, currentDashboardImage,
  applyDashboardImageControls, currentDashboardImageFit, currentDashboardImagePosition,
  DASH_IMAGE_ALLOWED_EXTENSIONS, DASH_IMAGE_ALLOWED_TYPES, DASH_IMAGE_FITS,
  DASH_IMAGE_LOCAL_STORAGE_MAX_CHARS, DASH_IMAGE_REMOTE_MAX_BYTES, DASH_IMAGE_UPLOAD_MAX_BYTES,
  dashboardImageExtensionFromUrl, dashboardImageUrlHasAllowedExtension,
  isAllowedDashboardImageFileType,
  applyCompactWindows, currentCompactWindows,
} from './themeSwitcher.helper.js';
import taskbarStore from '../lib/taskbarStore.js';
import './AddModuleLauncher.css';
import './HomeMenu.css';
import CrtTooltip from './CrtTooltip.jsx';
import ThemedIndicatorLight from './ThemedIndicatorLight.jsx';
import SettingsHome from './SettingsHome.jsx';
import RelaySetupDialog from './RelaySetupDialog.jsx';
import { tipProps, tooltip } from './MouseTooltip.jsx';
import { EMOJI } from '../lib/emoji.js';

const ENTITY_DETAIL_TYPES = new Set(['item-detail', 'guild-detail']);
import { hints, HINT_LEVELS, HINT_DELAY_MIN, HINT_DELAY_MAX } from '../lib/hintsStore.js';
import { getSchema } from '../widgets/settingsSchemas.js';

// Issue #95 — append/normalise a Discord CDN size hint so we don't pull
// 1024px PNGs for a tiny widget. Returns the URL untouched if it already
// has a query string (e.g. forced by an upstream override).
function withAvatarSize(url, size) {
  if (!url) return url;
  if (url.includes('?')) return url;
  return `${url}?size=${size || 64}`;
}

const CATEGORY_META = {
  personal: { label: 'Personal',       color: '#22d3ee' },
  guild:    { label: 'Guild',          color: '#4ade80' },
  tools:    { label: 'Tools',          color: '#d4a24e' },
  family:   { label: 'Family',         color: '#c084fc' },
  admin:    { label: 'Administration', color: '#ff4d2d' },
  other:    { label: 'Other',          color: '#9ca3af' },
};
const CATEGORY_ORDER = ['personal', 'guild', 'tools', 'family', 'admin', 'other'];
const LORE_THEME_IDS = new Set(['khajiit', 'dunmer', 'orsimer', 'necrom', 'blackwood']);
const KEY_THEME_LIVE_DESKTOP_PREVIEW = 'redfur:v2:themeLiveDesktopPreview';
const THEME_EDITOR_MIN_WIDTH = 640;
const THEME_EDITOR_MAX_WIDTH = 940;
const THEME_EDITOR_MIN_HEIGHT = 420;
const THEME_EDITOR_VIEWPORT_PAD = 16;

const TABS = [
  { id: 'widgets',    label: 'Widgets',  icon: EMOJI.widgets,    anon: true  },
  { id: 'theme',      label: 'Theme',    icon: EMOJI.theme,      anon: true  },
  { id: 'settings',   label: 'Settings', icon: EMOJI.settings,   anon: false },
  { id: 'account',    label: 'Account',  icon: EMOJI.account,    anon: true  },
  { id: 'layout',     label: 'Layout',   icon: EMOJI.layout,     anon: false },
  // #338 — Dashboards tab merges desktop-tab switching into the home menu.
  { id: 'dashboards', label: 'Desks',    icon: EMOJI.dashboards, anon: true  },
];

// Issue #116 — build a hover tooltip from the widget contract
// (`title`, `description`) plus a quick summary of its settings keys
// pulled from settingsSchemas. Keeps the catalog row terse while still
// teaching what the widget controls.
function widgetTipText(w) {
  const lines = [];
  if (w.title) lines.push(w.title);
  if (w.tier === 'owner') lines.push('🔒 Owner-only — hidden from regular users');
  else if (w.tier === 'admin') lines.push('🛡️ Staff-only — hidden from regular users');
  else if (w.tier === 'family') lines.push('🏠 Family-only — restricted to Echo & household family');
  if (w.description) lines.push(w.description);
  const fields = getSchema(w.type);
  if (Array.isArray(fields) && fields.length) {
    const labels = fields
      .filter((f) => !f.adminOnly) // hide admin-only keys from the public tip
      .map((f) => f.label || f.key)
      .filter(Boolean);
    if (labels.length) {
      lines.push('');
      lines.push('⚙ Settings: ' + labels.join(', '));
    }
  }
  return lines.join('\n');
}

function launcherCategory(w) {
  if (w?.type === 'fissal-open-webui' || w?.type === 'fissal-ai') return 'guild';
  const cat = w?.category || 'other';
  return CATEGORY_META[cat] ? cat : 'other';
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

export default function HomeMenu() {
  const open = homeMenu.open;
  const setOpen = homeMenu.setOpen;
  const [tab, setTab] = createSignal('widgets');
  const [query, setQuery] = createSignal('');
  const [theme, setThemeSig] = createSignal(currentTheme());
  const [themeOverride, setThemeOverride] = createSignal(currentThemeOverride(currentTheme()));
  const [customEditorOpen, setCustomEditorOpen] = createSignal(false);
  const [dashImage, setDashImage] = createSignal(currentDashboardImage());
  const initialDashPosition = currentDashboardImagePosition();
  const [dashFit, setDashFit] = createSignal(currentDashboardImageFit());
  const [dashPosX, setDashPosX] = createSignal(initialDashPosition.x);
  const [dashPosY, setDashPosY] = createSignal(initialDashPosition.y);
  // Issue #311 — surface validation feedback for the dashboard image URL.
  // `kind` is one of: '' (clean), 'error' (rejected), 'warn' (soft-allowed).
  const [dashImageMsg, setDashImageMsg] = createSignal({ kind: '', text: '' });
  const [dashImageBusy, setDashImageBusy] = createSignal(false);
  const [compact, setCompact] = createSignal(currentCompactWindows());
  const [glow, setGlowSig] = createSignal(currentGlowPercent());
  const [neon, setNeonSig] = createSignal(currentNeonPercent());
  const [wmBorder, setWmBorderSig] = createSignal(currentWmBorderWidth());
  const [fontScale, setFontScaleSig] = createSignal(currentFontScalePercent());
  const [customEditorRect, setCustomEditorRect] = createSignal(null);
  const [liveDesktopPreview, setLiveDesktopPreview] = createSignal(false);
  const [liveDesktopAvailable, setLiveDesktopAvailable] = createSignal(true);
  const [liveDesktopPreferenceLoaded, setLiveDesktopPreferenceLoaded] = createSignal(false);
  let previewRaf = null;
  let pendingPreviewDraft = null;
  let customEditorDragCleanup = null;

  function dismissHomeTooltips() {
    tooltip.unpin();
  }

  function shouldUseSafePreviewOnly() {
    if (typeof window === 'undefined') return true;
    const memory = Number(window.navigator?.deviceMemory || 0);
    const cores = Number(window.navigator?.hardwareConcurrency || 0);
    const reducedMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    return reducedMotion || (memory > 0 && memory <= 4) || (cores > 0 && cores <= 4);
  }

  function scheduleLiveDesktopPreview(draft) {
    if (!liveDesktopPreview() || !liveDesktopAvailable()) return;
    pendingPreviewDraft = draft;
    if (previewRaf != null || typeof window === 'undefined') return;
    previewRaf = window.requestAnimationFrame(() => {
      previewRaf = null;
      if (!pendingPreviewDraft) return;
      previewThemeOverride(theme(), pendingPreviewDraft);
    });
  }

  function stopLiveDesktopPreview() {
    if (previewRaf != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(previewRaf);
      previewRaf = null;
    }
    pendingPreviewDraft = null;
    clearThemeOverridePreview(theme());
  }

  function closeCustomEditor() {
    if (customEditorDragCleanup) {
      customEditorDragCleanup();
      customEditorDragCleanup = null;
    }
    setCustomEditorOpen(false);
    stopLiveDesktopPreview();
  }

  function themeEditorViewport() {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    return {
      width: Math.max(320, viewportWidth),
      height: Math.max(320, viewportHeight),
    };
  }

  function clampThemeEditorRect(rect) {
    const viewport = themeEditorViewport();
    const maxWidth = Math.max(340, viewport.width - THEME_EDITOR_VIEWPORT_PAD * 2);
    const width = Math.min(maxWidth, Math.max(Math.min(THEME_EDITOR_MIN_WIDTH, maxWidth), Number(rect?.width) || maxWidth));
    const maxHeight = Math.max(280, viewport.height - THEME_EDITOR_VIEWPORT_PAD * 2);
    const requestedHeight = Number(rect?.height) || Math.round(viewport.height * 0.78);
    const height = Math.min(maxHeight, Math.max(Math.min(THEME_EDITOR_MIN_HEIGHT, maxHeight), requestedHeight));
    const left = Math.min(
      Math.max(THEME_EDITOR_VIEWPORT_PAD, Number(rect?.left) || THEME_EDITOR_VIEWPORT_PAD),
      Math.max(THEME_EDITOR_VIEWPORT_PAD, viewport.width - width - THEME_EDITOR_VIEWPORT_PAD),
    );
    const top = Math.min(
      Math.max(THEME_EDITOR_VIEWPORT_PAD, Number(rect?.top) || THEME_EDITOR_VIEWPORT_PAD),
      Math.max(THEME_EDITOR_VIEWPORT_PAD, viewport.height - height - THEME_EDITOR_VIEWPORT_PAD),
    );
    return {
      left: Math.round(left),
      top: Math.round(top),
      width: Math.round(width),
      height: Math.round(height),
    };
  }

  function buildThemeEditorRect(sourceEl) {
    const viewport = themeEditorViewport();
    const maxWidth = Math.max(340, viewport.width - THEME_EDITOR_VIEWPORT_PAD * 2);
    const width = Math.min(THEME_EDITOR_MAX_WIDTH, Math.max(Math.min(THEME_EDITOR_MIN_WIDTH, maxWidth), Math.round(viewport.width * 0.72)));
    const maxHeight = Math.max(280, viewport.height - THEME_EDITOR_VIEWPORT_PAD * 2);
    const height = Math.min(maxHeight, Math.max(Math.min(THEME_EDITOR_MIN_HEIGHT, maxHeight), Math.round(viewport.height * 0.78)));
    const src = sourceEl?.getBoundingClientRect?.();
    const suggestedLeft = src
      ? Math.round(src.left + (src.width / 2) - (width / 2))
      : Math.round((viewport.width - width) / 2);
    const suggestedTop = src
      ? Math.round(src.top - 28)
      : Math.round((viewport.height - height) / 2);
    return clampThemeEditorRect({
      left: suggestedLeft,
      top: suggestedTop,
      width,
      height,
    });
  }

  function repositionCustomEditor() {
    if (!customEditorOpen()) return;
    setCustomEditorRect((prev) => clampThemeEditorRect(prev || {}));
  }

  function beginCustomEditorDrag(event) {
    if (event.button !== 0) return;
    const startRect = customEditorRect();
    if (!startRect) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const originLeft = startRect.left;
    const originTop = startRect.top;
    const move = (moveEvent) => {
      const next = clampThemeEditorRect({
        ...startRect,
        left: originLeft + (moveEvent.clientX - startX),
        top: originTop + (moveEvent.clientY - startY),
      });
      setCustomEditorRect(next);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (customEditorDragCleanup === end) customEditorDragCleanup = null;
    };
    if (customEditorDragCleanup) customEditorDragCleanup();
    customEditorDragCleanup = end;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  createEffect(() => {
    if (open()) document.body.classList.add('v2-homemenu-open');
    else {
      document.body.classList.remove('v2-homemenu-open');
      closeCustomEditor();
      dismissHomeTooltips();
    }
  });

  onMount(() => {
    const safeOnly = shouldUseSafePreviewOnly();
    setLiveDesktopAvailable(!safeOnly);
    if (safeOnly) {
      setLiveDesktopPreview(false);
      setLiveDesktopPreferenceLoaded(true);
      return;
    }
    try {
      const stored = localStorage.getItem(KEY_THEME_LIVE_DESKTOP_PREVIEW);
      setLiveDesktopPreview(stored === 'true');
    } catch {
      setLiveDesktopPreview(false);
    }
    setLiveDesktopPreferenceLoaded(true);
  });

  createEffect(() => {
    if (typeof window === 'undefined') return;
    if (!liveDesktopPreferenceLoaded()) return;
    try {
      localStorage.setItem(KEY_THEME_LIVE_DESKTOP_PREVIEW, String(!!liveDesktopPreview()));
    } catch { /* ignore */ }
  });

  // #10 — when the taskbar is docked (always-visible) the menu opens from
  // the bottom-left Start button, so flag <body> to re-anchor the panel
  // above it (Windows Start-menu style). Auto-hide mode keeps the
  // top-right anchor used by the floating HamburgerTrigger.
  createEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('taskbar-docked', taskbarStore.alwaysVisible());
  });

  // Account / auth-me state — only fetched when the panel opens for the
  // first time and refreshed when the user actively switches to the tab.
  const [authMe, { refetch: refetchMe }] = createResource(
    () => open() && (tab() === 'account' || tab() === 'settings'),
    async () => {
      try {
        const r = await fetch('/auth/me', { credentials: 'include' });
        if (!r.ok) return null;
        return r.json();
      } catch { return null; }
    },
  );
  const [relaySetupOpen, setRelaySetupOpen] = createSignal(false);

  // Family-device probe — is this browser's device on the home LAN, and is
  // it flagged/approved as a family device? Lazy: only when Account opens.
  const [familyDevice, { refetch: refetchFamilyDevice }] = createResource(
    () => open() && tab() === 'account' ? 'probe' : null,
    async () => {
      try {
        const r = await fetch('/api/v2/family/device', { credentials: 'include' });
        if (!r.ok) return null;
        return r.json();
      } catch { return null; }
    },
  );
  const [flaggingDevice, setFlaggingDevice] = createSignal(false);

  async function flagThisDevice() {
    setFlaggingDevice(true);
    try {
      const r = await fetch('/api/v2/family/device/flag', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || 'Flag failed.');
      toast.success(json.already
        ? `Device already ${json.status}.`
        : 'Device flagged — a family member can approve it from the Family Devices widget.');
      refetchFamilyDevice();
    } catch (err) {
      toast.error(err.message);
    } finally { setFlaggingDevice(false); }
  }

  onMount(() => {
    const openRelaySetup = () => setRelaySetupOpen(true);
    window.addEventListener('v2-open-relay-setup', openRelaySetup);
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('relay') === '1') {
        setRelaySetupOpen(true);
        params.delete('relay');
        const query = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
      }
    } catch { /* dashboard link is best-effort */ }
    onCleanup(() => window.removeEventListener('v2-open-relay-setup', openRelaySetup));
  });

  // Per-user prefs (UI blob). Lazy-loaded once the panel opens.
  const [prefs, { mutate: setPrefs }] = createResource(
    () => open() && wm.state.viewer ? 'load' : null,
    async () => {
      try {
        const r = await api.getPrefs();
        return r?.prefs || {};
      } catch { return {}; }
    },
  );

  function openPanel(targetTab) {
    setQuery('');
    if (targetTab) setTab(targetTab);
    setOpen(true);
    queueMicrotask(() => {
      if (tab() === 'widgets') {
        document.querySelector('.v2-launcher__search input')?.focus();
      }
    });
  }

  createEffect(() => {
    // Issue #311 — only apply when the URL was already validated/seeded.
    // Validation now lives in setDashboardImage(); reading dashImage here
    // still keeps live previews working when the signal is initialised
    // from currentDashboardImage() (no error state set).
    if (dashImageMsg().kind === 'error') return;
    applyDashboardImage(dashImage(), dashboardImageControls());
  });

  createEffect(() => {
    applyCompactWindows(compact());
  });

  createEffect(() => {
    const prefsState = prefs();
    if (prefsState && typeof prefsState.compactWindows === 'boolean') {
      const next = !!prefsState.compactWindows;
      setCompact(next);
      applyCompactWindows(next);
    }
  });

  function dashboardImageControls() {
    return { fit: dashFit(), x: dashPosX(), y: dashPosY() };
  }

  function useDashboardImage(value, message) {
    const applied = applyDashboardImage(value, dashboardImageControls());
    setDashImage(applied);
    if (message) setDashImageMsg(message);
    return applied;
  }

  function rejectDashboardImage(text, detail) {
    setDashImageMsg({ kind: 'error', text });
    toast.error('Dashboard image rejected', { detail, ttl: 5000 });
  }

  function setDashboardImage(value) {
    const next = (value || '').trim();
    setDashImage(next);
    if (!next) {
      useDashboardImage('', { kind: '', text: '' });
      toast.info('Dashboard image cleared', { ttl: 1200 });
      return;
    }

    let parsed = null;
    try { parsed = new URL(next); } catch { /* invalid */ }
    if (!parsed || !/^https?:$/.test(parsed.protocol)) {
      rejectDashboardImage('Use a full http(s) URL, e.g. https://i.imgur.com/abc.png', 'Not a valid http(s) URL');
      return;
    }

    const extension = dashboardImageExtensionFromUrl(next);
    if (extension && !DASH_IMAGE_ALLOWED_EXTENSIONS.includes(extension)) {
      rejectDashboardImage(
        `Allowed image formats: ${DASH_IMAGE_ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(', ')}`,
        `.${extension} is not allowed`,
      );
      return;
    }

    setDashImageBusy(true);
    setDashImageMsg({ kind: '', text: '' });
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    fetch(next, { method: 'HEAD', mode: 'cors', signal: ctrl.signal })
      .then((r) => {
        clearTimeout(timer);
        const ct = (r.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
        const length = parseInt(r.headers.get('content-length') || '', 10);
        if (!r.ok) {
          rejectDashboardImage('That URL did not respond successfully.', `status ${r.status}`);
          return;
        }
        if (ct && !isAllowedDashboardImageFileType(ct)) {
          rejectDashboardImage(
            `Allowed image formats: ${DASH_IMAGE_ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(', ')}`,
            `content-type: ${ct || 'unknown'}`,
          );
          return;
        }
        if (Number.isFinite(length) && length > DASH_IMAGE_REMOTE_MAX_BYTES) {
          rejectDashboardImage(
            `Remote images must be ${formatBytes(DASH_IMAGE_REMOTE_MAX_BYTES)} or smaller.`,
            `content-length: ${formatBytes(length)}`,
          );
          return;
        }
        if (ct || dashboardImageUrlHasAllowedExtension(next)) {
          useDashboardImage(next, { kind: '', text: '' });
          toast.success('Dashboard image set', { ttl: 1800 });
        } else {
          rejectDashboardImage('Could not confirm an allowed image format. Use a direct .png, .jpg, .webp, .gif, or .avif link.', 'missing content-type and file extension');
        }
      })
      .catch(() => {
        clearTimeout(timer);
        if (!dashboardImageUrlHasAllowedExtension(next)) {
          rejectDashboardImage('Could not verify this URL. Use a direct .png, .jpg, .webp, .gif, or .avif link.', 'HEAD blocked and extension missing');
          return;
        }
        useDashboardImage(next, {
          kind: 'warn',
          text: 'Could not verify size headers (CORS or offline). Direct image extension accepted with upload limits still enforced for local files.',
        });
        toast.warn('Dashboard image set (unverified)', { ttl: 3500 });
      })
      .finally(() => setDashImageBusy(false));
  }

  function setDashboardImageFit(value) {
    setDashFit(value);
    applyDashboardImageControls({ fit: value, x: dashPosX(), y: dashPosY() });
  }

  function setDashboardImagePosition(axis, value) {
    const next = Number(value) || 0;
    if (axis === 'x') setDashPosX(next);
    else setDashPosY(next);
    applyDashboardImageControls({ fit: dashFit(), x: axis === 'x' ? next : dashPosX(), y: axis === 'y' ? next : dashPosY() });
  }

  function onDashboardImageUpload(event) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!isAllowedDashboardImageFileType(file.type)) {
      rejectDashboardImage(
        `Allowed upload formats: ${DASH_IMAGE_ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(', ')}`,
        file.type || 'unknown file type',
      );
      return;
    }
    if (file.size > DASH_IMAGE_UPLOAD_MAX_BYTES) {
      rejectDashboardImage(
        `Uploads must be ${formatBytes(DASH_IMAGE_UPLOAD_MAX_BYTES)} or smaller.`,
        `${file.name}: ${formatBytes(file.size)}`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (dataUrl.length > DASH_IMAGE_LOCAL_STORAGE_MAX_CHARS) {
        rejectDashboardImage('Uploaded image is too large for browser storage after encoding. Try a smaller or compressed image.', `${formatBytes(dataUrl.length)} encoded`);
        return;
      }
      useDashboardImage(dataUrl, {
        kind: 'warn',
        text: `Uploaded image cached in this browser (${formatBytes(file.size)} source).`,
      });
      toast.success('Dashboard image uploaded', { ttl: 1800 });
    };
    reader.onerror = () => rejectDashboardImage('Could not read that image file.', reader.error?.message || 'FileReader failed');
    reader.readAsDataURL(file);
  }
  function closePanel() { closeCustomEditor(); dismissHomeTooltips(); setOpen(false); }
  function toggle() { open() ? closePanel() : openPanel(); }

  // Esc closes the panel.
  function onKey(e) {
    if (!open()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      if (customEditorOpen()) {
        closeCustomEditor();
        return;
      }
      closePanel();
    }
  }
  // Click outside the FAB / panel closes it. We attach during the
  // capture phase so the listener fires before nested handlers can
  // stopPropagation, but we still respect the panel's own
  // stopPropagation by checking ancestry.
  // #336 — also exempt .v2-taskbar__start so the Start button can
  // reliably toggle the panel without an immediate close.
  function onDocClick(e) {
    if (!open()) return;
    const t = e.target;
    if (t.closest('.v2-launcher__panel') || t.closest('.v2-home__theme-popover') || t.closest('.v2-home__theme-popover-backdrop') || t.closest('.v2-launcher__btn') || t.closest('.v2-taskbar__start')) return;
    closePanel();
  }
  onMount(() => {
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDocClick, true);
    window.addEventListener('resize', repositionCustomEditor);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('pointerdown', onDocClick, true);
    window.removeEventListener('resize', repositionCustomEditor);
    if (customEditorDragCleanup) customEditorDragCleanup();
  });

  // ── Widgets tab ────────────────────────────────────────────────────
  const filtered = createMemo(() => {
    const q = query().trim().toLowerCase();
    const cat = (wm.state.catalog || []).filter(w => !ENTITY_DETAIL_TYPES.has(w.type) && !w.launcherHidden);
    if (!q) return cat;
    return cat.filter(w => {
      const hay = [w.title || '', w.description || '', w.type || '', launcherCategory(w)].join(' ').toLowerCase();
      return hay.includes(q);
    });
  });
  const grouped = createMemo(() => {
    const out = {};
    for (const w of filtered()) {
      const c = launcherCategory(w);
      (out[c] ||= []).push(w);
    }
    for (const items of Object.values(out)) {
      items.sort((a, b) => String(a.title || a.type).localeCompare(String(b.title || b.type)));
    }
    return out;
  });
  const orderedCategories = createMemo(() => CATEGORY_ORDER.filter(c => grouped()[c]?.length));
  function addAndClose(type) {
    wm.addWidget(type);
    closePanel();
  }
  function switchTab(nextTab) {
    dismissHomeTooltips();
    setTab(nextTab);
    if (nextTab !== 'theme') setCustomEditorOpen(false);
  }
  function onSearchKey(e) {
    if (e.key === 'Enter') {
      const first = filtered()[0];
      if (first) { e.preventDefault(); addAndClose(first.type); }
    }
  }

  // ── Theme tab ──────────────────────────────────────────────────────
  function pickTheme(id) {
    setTheme(id);
    setThemeSig(id);
    setThemeOverride(currentThemeOverride(id));
    closeCustomEditor();
    toast.success(`Theme: ${THEMES.find(t => t.id === id)?.label || id}`, { ttl: 1800 });
  }

  function previewCustomThemeValue(key, value) {
    const next = { ...(themeOverride() || {}), [key]: value };
    setThemeOverride(next);
    scheduleLiveDesktopPreview(next);
  }

  function commitCustomThemeValue(key, value) {
    stopLiveDesktopPreview();
    const next = applyThemeOverride(theme(), { [key]: value });
    setThemeOverride(next);
  }

  function resetCustomThemeValues() {
    stopLiveDesktopPreview();
    const next = resetThemeOverride(theme());
    setThemeOverride(next);
    toast.info(`${THEMES.find(t => t.id === theme())?.label || 'Theme'} colors reset`, { ttl: 1600 });
  }

  function toggleCustomEditor(themeDef, event) {
    event.stopPropagation();
    setThemeOverride(currentThemeOverride(themeDef.id));
    const nextOpen = !customEditorOpen();
    if (!nextOpen) {
      closeCustomEditor();
      return;
    }
    setCustomEditorRect(buildThemeEditorRect(event.currentTarget));
    setCustomEditorOpen(true);
  }

  function customEditorStyle() {
    const rect = customEditorRect();
    if (!rect) return {};
    return {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      'max-height': `${rect.height}px`,
    };
  }

  function customThemePreviewStyle() {
    const preview = themeOverride() || {};
    return {
      '--preview-bezel': preview.bezel,
      '--preview-bezel-edge': preview.bezelEdge,
      '--preview-screen-bg': preview.screenBg,
      '--preview-screen-bg-2': preview.screenBg2,
      '--preview-window-bg': preview.windowBg,
      '--preview-window-edge': preview.windowEdge,
      '--preview-text': preview.text,
      '--preview-text-dim': preview.textDim,
      '--preview-accent': preview.fissal,
      '--preview-focus': preview.focusAccent,
      '--preview-gold': preview.gold,
      '--preview-cyan': preview.cyan,
    };
  }

  function renderCustomThemeEditor() {
    return (
      <div class="v2-home__theme-popover-backdrop" onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        closeCustomEditor();
      }}>
        <div
          id="v2-theme-editor"
          class="v2-home__custom-editor v2-home__theme-popover"
          role="dialog"
          aria-modal="true"
          aria-label={`Customize ${activeThemeLabel()} theme`}
          style={customEditorStyle()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div class="v2-home__theme-popover-header" onPointerDown={beginCustomEditorDrag}>
            <div class="v2-home__theme-popover-title">Customize {activeThemeLabel()}</div>
            <button
              type="button"
              class="v2-home__theme-popover-close"
              aria-label="Close theme customizer"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={closeCustomEditor}
            >✕</button>
          </div>
          <div class="v2-home__hint v2-home__hint--small">Drag this popup to keep it visible while tuning your colors.</div>
          <label class="v2-home__theme-live-toggle">
            <input
              type="checkbox"
              checked={liveDesktopPreview()}
              disabled={!liveDesktopAvailable()}
              onChange={(event) => {
                const enabled = !!event.currentTarget.checked && liveDesktopAvailable();
                setLiveDesktopPreview(enabled);
                if (!enabled) stopLiveDesktopPreview();
                else scheduleLiveDesktopPreview(themeOverride());
              }}
            />
            <span>Live desktop preview while dragging</span>
          </label>
          <Show when={!liveDesktopAvailable()}>
            <div class="v2-home__hint v2-home__hint--small">Live desktop preview is disabled on this device to keep interaction smooth.</div>
          </Show>
          <div class="v2-home__theme-preview" style={customThemePreviewStyle()} aria-hidden="true">
            <div class="v2-home__theme-preview-label">Live preview window</div>
            <div class="v2-home__theme-preview-screen">
              <div class="v2-home__theme-preview-window">
                <div class="v2-home__theme-preview-title">Trader Console</div>
                <div class="v2-home__theme-preview-body">
                  <p>Sample seller text</p>
                  <div class="v2-home__theme-preview-meta">Avg sale: 31,400g</div>
                  <button type="button" class="v2-home__theme-preview-btn">Action</button>
                </div>
              </div>
            </div>
          </div>
          <div class="v2-home__custom-grid">
            <For each={CUSTOM_THEME_FIELDS}>{(field) => (
              <label class="v2-home__color-row">
                <span>{field.label}</span>
                <input
                  type="color"
                  value={themeOverride()[field.key]}
                  onInput={(event) => previewCustomThemeValue(field.key, event.currentTarget.value)}
                  onChange={(event) => commitCustomThemeValue(field.key, event.currentTarget.value)}
                  aria-label={`${activeThemeLabel()} ${field.label}`}
                />
              </label>
            )}</For>
          </div>
          <div class="v2-home__theme-actions">
            <button type="button" class="v2-home__btn" onClick={resetCustomThemeValues}>Reset this theme</button>
          </div>
        </div>
      </div>
    );
  }

  function themeSwatch(themeDef) {
    return storedThemeOverride(themeDef.id)?.fissal || themeDef.swatch;
  }

  function activeThemeLabel() {
    return THEMES.find(themeDef => themeDef.id === theme())?.label || 'Theme';
  }

  function renderThemeOption(themeDef) {
    const isActive = () => theme() === themeDef.id;
    return (
      <div
        class="v2-home__theme-opt"
        classList={{ 'is-active': isActive() }}
        onClick={() => pickTheme(themeDef.id)}
      >
        <button type="button" class="v2-home__theme-pick" onClick={(event) => { event.stopPropagation(); pickTheme(themeDef.id); }}>
          <span class="v2-home__theme-swatch" style={{ background: themeSwatch(themeDef) }} />
          <span class="v2-home__theme-copy">
            <span class="v2-home__theme-name">{themeDef.label}</span>
            <span class="v2-home__theme-desc">{themeDef.desc}</span>
          </span>
        </button>
        <Show when={isActive()}>
          <button
            type="button"
            class="v2-home__theme-gear"
            aria-label={`Customize ${themeDef.label} theme`}
            aria-controls="v2-theme-editor"
            aria-expanded={customEditorOpen()}
            title={`Customize ${themeDef.label}`}
            onClick={(event) => toggleCustomEditor(themeDef, event)}
          >⚙</button>
        </Show>
      </div>
    );
  }

  function pickGlow(pct) {
    const v = applyGlowPercent(pct);
    setGlowSig(v);
  }
  function pickNeon(pct) {
    const v = applyNeonPercent(pct);
    setNeonSig(v);
  }
  function pickBorder(px) {
    const v = applyWmBorderWidth(px);
    setWmBorderSig(v);
  }
  function pickFontScale(pct) {
    const v = applyFontScalePercent(pct);
    setFontScaleSig(v);
  }

  // ── Settings tab — UI prefs ────────────────────────────────────────
  async function savePrefPatch(patch) {
    const next = { ...(prefs() || {}), ...patch };
    setPrefs(next);
    if ('compactWindows' in patch) {
      const nextCompact = !!patch.compactWindows;
      setCompact(nextCompact);
      applyCompactWindows(nextCompact);
    }
    try {
      await api.putPrefs(next);
      toast.info('Preference saved', { ttl: 1500 });
    } catch (err) {
      toast.error('Save failed', { detail: err?.message || 'unknown error' });
    }
  }

  // Issue #143 — avatar privacy toggle. The viewer payload from
  // /api/v2/health already carries `hideAvatar`, so we initialise from
  // that and just reflect the new state on the local signal after PUT.
  const [hideAvatar, setHideAvatar] = createSignal(!!wm.state.viewer?.hideAvatar);
  async function toggleHideAvatar(hide) {
    setHideAvatar(!!hide);
    try {
      await api.putAvatarPrivacy(!!hide);
      toast.info(hide ? 'Avatar hidden from public view' : 'Avatar visible', { ttl: 1500 });
    } catch (err) {
      setHideAvatar(!hide);
      toast.error('Save failed', { detail: err?.message || 'unknown error' });
    }
  }

  // Issue #348 — admin guild-scope toggle. Persisted server-side in the
  // session (resets on logout). Changing the scope reloads the page so
  // all widget data is fetched with the new guild filter applied.
  const [adminViewAllGuilds, setAdminViewAllGuilds] = createSignal(
    !!wm.state.identity?.adminViewAllGuilds,
  );
  async function toggleAdminViewAll(all) {
    setAdminViewAllGuilds(!!all);
    try {
      await api.putGuildScope(!!all);
      toast.info(
        all ? 'Showing all tracked guilds — reloading…' : 'Redfur guilds only — reloading…',
        { ttl: 1500 },
      );
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setAdminViewAllGuilds(!all);
      toast.error('Save failed', { detail: err?.message || 'unknown error' });
    }
  }

  // ── Account tab ────────────────────────────────────────────────────
  function logout() {
    location.assign('/auth/logout');
  }

  const testingGround = () => wm.state.testingGround?.enabled ? wm.state.testingGround : null;
  const [switchingPersona, setSwitchingPersona] = createSignal('');

  async function switchTestingGroundPersona(slug) {
    if (!slug || switchingPersona()) return;
    setSwitchingPersona(slug);
    try {
      await api.switchTestingGround(slug);
      window.location.assign('/v2/');
    } catch (err) {
      setSwitchingPersona('');
      toast.error('Could not switch testing-ground persona', { detail: err?.message || 'unknown error' });
    }
  }

  // ── Family device section (Account tab) ─────────────────────────────
  // Shown only when the browser's device is on the home LAN. Lets the
  // device self-flag as a family device (pending approval by an existing
  // family member from the Family Devices widget).
  function renderFamilyDeviceSection() {
    return (
      <Show when={familyDevice()?.onLan && familyDevice()?.enrollmentEnabled !== false && familyDevice()?.joinFamilyVisible !== false}>
        <hr />
        <p class="v2-home__hint">Family access</p>
        <Show
          when={!familyDevice()?.known}
          fallback={
            <p class="v2-home__hint v2-home__hint--small">
              {familyDevice()?.status === 'approved'
                ? '✅ This device is an approved family device.'
                : familyDevice()?.status === 'pending'
                  ? '⏳ This device is flagged and waiting for a family member to approve it.'
                  : '🚫 This device was revoked by a family member.'}
            </p>
          }
        >
          <p class="v2-home__hint v2-home__hint--small">
            You're on the home network. Flag this device to request access to
            the family dashboard (photos, cameras, home AI).
          </p>
          <button
            type="button"
            class="v2-home__btn v2-home__btn--primary"
            disabled={flaggingDevice()}
            onClick={flagThisDevice}
          >
            {flaggingDevice() ? 'Flagging…' : '🏠 Flag this device as family'}
          </button>
        </Show>
      </Show>
    );
  }

  function renderTestingGroundSection() {
    return (
      <Show when={testingGround()}>
        <hr />
        <p class="v2-home__hint">Testing ground</p>
        <p class="v2-home__hint v2-home__hint--small">
          Local-only persona switching backed by fixture data. Use this before shipping changes live.
        </p>
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <For each={testingGround()?.personas || []}>{(persona) => (
            <button
              type="button"
              class="v2-home__btn"
              classList={{ 'v2-home__btn--primary': testingGround()?.currentPersona === persona.slug }}
              disabled={switchingPersona() === persona.slug}
              onClick={() => switchTestingGroundPersona(persona.slug)}
              title={persona.description || persona.label}
            >
              {switchingPersona() === persona.slug ? 'Switching…' : persona.label}
              <Show when={persona.esoTag}> {` · ${persona.esoTag}`}</Show>
            </button>
          )}</For>
        </div>
      </Show>
    );
  }

  // ── Layout tab ─────────────────────────────────────────────────────
  // #344 — destructive "Reset to defaults" gets a near-cursor inline
  // confirm instead of firing immediately. First click opens a small
  // popover at the pointer; a second click on Confirm commits. Mouse-leave
  // or a 3s idle fades it away. CSP-safe (Solid event bindings, no inline
  // handlers).
  const [resetConfirm, setResetConfirm] = createSignal(null); // { x, y }
  let resetConfirmTimer = null;
  function clearResetConfirm() {
    if (resetConfirmTimer) { clearTimeout(resetConfirmTimer); resetConfirmTimer = null; }
    setResetConfirm(null);
  }
  function onResetClick(e) {
    e.preventDefault();
    if (resetConfirm()) return; // already open; Confirm/Cancel handle it
    setResetConfirm({ x: e.clientX, y: e.clientY });
    if (resetConfirmTimer) clearTimeout(resetConfirmTimer);
    resetConfirmTimer = setTimeout(clearResetConfirm, 3000);
  }
  function confirmReset() {
    clearResetConfirm();
    wm.resetLayout();
    closePanel();
  }
  function doRestoreAll() {
    wm.restoreAll();
    toast.info('Restored all windows');
  }
  async function saveSpyTemplate() {
    const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
    if (!dash || dash.id === 'anon') { toast.warn('Switch to a real tab first'); return; }
    const template = (dash.widgets || []).map(w => ({
      widget_type: w.widget_type,
      x: w.x, y: w.y, w: w.w, h: w.h, z: w.z,
      minimized: !!w.minimized, maximized: !!w.maximized,
      settings: w.settings || {},
    }));
    try {
      await api.putSpyTemplate(template);
      toast.success(`Spy template saved (${template.length} widgets)`);
    } catch (err) {
      toast.error('Save spy template failed', { detail: err?.message || 'unknown error' });
    }
  }

  // ── Dashboards tab (#338) ──────────────────────────────────────
  async function addDash() {
    const name = prompt('Dashboard name?', 'New Tab');
    if (!name) return;
    const emoji = prompt('Emoji?', EMOJI.window) || EMOJI.window;
    await wm.addDashboard(name, emoji);
  }  async function deleteDash(d) {
    if (wm.state.dashboards.length <= 1) {
      toast.warn('Cannot delete your last dashboard.');
      return;
    }
    if (!confirm(`Delete tab "${d.name}"? Its widgets will be removed.`)) return;
    try { await wm.deleteDashboard(d.id); }
    catch { /* error toast already raised by store */ }
  }
  async function renameDash(d) {
    const name = prompt('Rename desktop', d.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === d.name) return;
    try { await wm.renameDashboard(d.id, { name: trimmed, emoji: d.emoji }); }
    catch { /* error toast already raised by store */ }
  }

  // Drag-to-reorder desktops (#3 — restores the feature lost when the
  // TabRail was unmounted). Native HTML5 drag-and-drop keeps it CSP-safe
  // (no inline handlers); only logged-in, non-anon, non-spy tabs reorder.
  const [dragDashId, setDragDashId] = createSignal(null);
  const [overDashId, setOverDashId] = createSignal(null);
  const canReorderDash = (d) => !!wm.state.viewer && d.id !== 'anon' && !d._spyTab;
  function onDashDragStart(e, d) {
    if (!canReorderDash(d)) return;
    setDragDashId(d.id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox requires data to be set for a drag to initialise.
      e.dataTransfer.setData('text/plain', d.id);
    } catch { /* dataTransfer may be unavailable in some envs */ }
  }
  function onDashDragOver(e, d) {
    if (!dragDashId() || !canReorderDash(d)) return;
    e.preventDefault();               // allow the drop
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* noop */ }
    if (overDashId() !== d.id) setOverDashId(d.id);
  }
  function onDashDrop(e, d) {
    e.preventDefault();
    const dragged = dragDashId();
    setDragDashId(null);
    setOverDashId(null);
    if (!dragged || !canReorderDash(d) || dragged === d.id) return;
    const ids = wm.state.dashboards.filter(x => x.id !== 'anon').map(x => x.id);
    const from = ids.indexOf(dragged);
    const to = ids.indexOf(d.id);
    if (from === -1 || to === -1) return;
    ids.splice(from, 1);
    ids.splice(to, 0, dragged);
    wm.reorderDashboards(ids);
  }
  function onDashDragEnd() {
    setDragDashId(null);
    setOverDashId(null);
  }

  // ── Helpers ────────────────────────────────────────────────────────
  const visibleTabs = createMemo(() => TABS.filter(t => t.anon || wm.state.viewer));
  const isAnon = () => !wm.state.viewer;
  const isAdmin = () => !!wm.state.identity?.isAdmin;

  // Allow external triggers (e.g. TopBar) to request a specific tab
  // via homeMenu.openWith(tabId).
  createEffect(() => {
    const t = homeMenu.pendingTab();
    if (t) {
      switchTab(t);
      homeMenu.setPendingTab(null);
    }
  });

  return (
    <>
      <Show when={open()}>
        <div class="v2-launcher__panel v2-home" onClick={(e) => e.stopPropagation()}>
          <header class="v2-home__hdr">
            <span>Home</span>
            <button onClick={closePanel} title="Close (Esc)">✖</button>
          </header>

          <nav class="v2-home__tabs" role="tablist">
            <For each={visibleTabs()}>{(t) => (
              <button
                role="tab"
                class="v2-home__tab"
                classList={{ 'is-active': tab() === t.id }}
                onClick={() => switchTab(t.id)}
              >
                <span class="v2-home__tab-icon">{t.icon}</span>
                <span>{t.label}</span>
              </button>
            )}</For>
          </nav>

          {/* ─── Widgets ───────────────────────────────────────────── */}
          <Show when={tab() === 'widgets'}>
            <div class="v2-home__pane">
              <div class="v2-launcher__search">
                <input
                  type="search"
                  placeholder="Search modules…"
                  value={query()}
                  onInput={(e) => setQuery(e.currentTarget.value)}
                  onKeyDown={onSearchKey}
                  aria-label="Search modules"
                />
                <Show when={query()}>
                  <button class="v2-launcher__clear" onClick={() => setQuery('')} title="Clear">✖</button>
                </Show>
              </div>
              <p class="v2-home__hint v2-home__hint--small">Click to open a window, or drag onto the desktop to create a shortcut icon.</p>
              <Show
                when={filtered().length > 0}
                fallback={<p class="v2-launcher__empty">No modules match “{query()}”.</p>}
              >
                <For each={orderedCategories()}>{(cat) => (
                  <section style={{ '--cat-color': CATEGORY_META[cat]?.color || '#888' }}>
                    <h4>{CATEGORY_META[cat]?.label || cat}</h4>
                    <ul>
                      <For each={grouped()[cat]}>{(w) => (
                        <li
                          draggable="true"
                          class={`v2-home__widget-item${w.tier === 'admin' || w.tier === 'owner' ? ' is-staff-locked' : ''}${w.tier === 'owner' ? ' is-owner-locked' : ''}`}
                          {...tipProps({ content: `${widgetTipText(w)}\n\nTip: Drag onto the desktop to create a shortcut icon.`, pin: false })}
                          onClick={() => addAndClose(w.type)}
                          onDragStart={(e) => {
                            dismissHomeTooltips();
                            try {
                              const payload = JSON.stringify({ type: w.type, title: w.title, emoji: w.emoji });
                              e.dataTransfer.effectAllowed = 'copy';
                              e.dataTransfer.setData('application/x-redfur-widget', payload);
                              e.dataTransfer.setData('text/x-redfur-widget', payload);
                              e.dataTransfer.setData('text/plain', payload);
                            } catch { /* ignore */ }
                            if (typeof document !== 'undefined') {
                              document.body.classList.add('v2-dragging-widget');
                            }
                          }}
                          onDragEnd={() => {
                            if (typeof document !== 'undefined') {
                              document.body.classList.remove('v2-dragging-widget');
                            }
                          }}
                        >
                          <span class="emo">{w.emoji}</span>
                          <span class="name">{w.title}</span>
                          <Show when={w.tier === 'admin' || w.tier === 'owner' || w.tier === 'family'}>
                            <span
                              class={`v2-home__staff-badge ${w.tier === 'owner' ? 'is-owner' : w.tier === 'family' ? 'is-family' : ''}`}
                              title={w.tier === 'owner' ? 'Owner-only — regular users cannot see this widget' : w.tier === 'family' ? 'Family-only — restricted to Echo & household family' : 'Staff-only — regular users cannot see this widget'}
                              aria-label={w.tier === 'owner' ? 'Owner only' : w.tier === 'family' ? 'Family only' : 'Staff only'}
                            >
                              {w.tier === 'family' ? '🏠 Family' : `🔒 ${w.tier === 'owner' ? 'Owner' : 'Staff'}`}
                            </span>
                          </Show>
                          <button
                            type="button"
                            class="v2-home__widget-pin"
                            title={`Pin ${w.title} shortcut to desktop`}
                            aria-label={`Pin ${w.title} shortcut to desktop`}
                            onClick={(e) => {
                              e.stopPropagation();
                              wm.addDesktopIcon({ widget_type: w.type, title: w.title, widgetType: w.type, label: w.title, emoji: w.emoji });
                            }}
                          >📌</button>
                        </li>
                      )}</For>
                    </ul>
                  </section>
                )}</For>
              </Show>
            </div>
          </Show>

          {/* ─── Theme ─────────────────────────────────────────────── */}
          <Show when={tab() === 'theme'}>
            <div class="v2-home__pane v2-home__theme">
              <p class="v2-home__hint">Pick an accent palette. Changes apply instantly and persist locally.</p>
              <section class="v2-home__theme-section">
                <h4>Core Themes</h4>
                <For each={THEMES.filter(themeDef => !LORE_THEME_IDS.has(themeDef.id))}>{(themeDef) => renderThemeOption(themeDef)}</For>
              </section>
              <section class="v2-home__theme-section">
                <h4>ESO &amp; DLC Themes</h4>
                <For each={THEMES.filter(themeDef => LORE_THEME_IDS.has(themeDef.id))}>{(themeDef) => renderThemeOption(themeDef)}</For>
              </section>
              <div class="v2-home__theme-image">
                <label>Dashboard image URL</label>
                <input
                  type="url"
                  class="v2-themed-select v2-home__theme-input"
                  placeholder="https://example.com/subtle-texture.png"
                  value={dashImage().startsWith('data:') ? '' : dashImage()}
                  onInput={(e) => { setDashImage(e.currentTarget.value); setDashImageMsg({ kind: '', text: '' }); }}
                  aria-label="Dashboard image URL"
                />
                <div class="v2-home__theme-actions">
                  <button type="button" class="v2-home__btn v2-home__btn--primary" disabled={dashImageBusy()} onClick={() => setDashboardImage(dashImage())}>
                    {dashImageBusy() ? 'Verifying…' : 'Apply image'}
                  </button>
                  <label class="v2-home__btn v2-home__upload-btn">
                    Upload image
                    <input
                      type="file"
                      accept={DASH_IMAGE_ALLOWED_TYPES.join(',')}
                      onChange={onDashboardImageUpload}
                      aria-label="Upload dashboard background image"
                    />
                  </label>
                  <button type="button" class="v2-home__btn" onClick={() => setDashboardImage('')}>Clear image</button>
                  <ThemedIndicatorLight
                    on={!!dashImage()}
                    tooltip={dashImage()
                      ? 'Dashboard background overridden — using your custom image instead of the theme default.'
                      : 'Using the theme\'s default background.'}
                    label="Background-overridden indicator"
                  />
                </div>
                <Show when={dashImageMsg().text}>
                  <p
                    class="v2-home__hint v2-home__hint--small"
                    classList={{
                      'v2-home__hint--error': dashImageMsg().kind === 'error',
                      'v2-home__hint--warn':  dashImageMsg().kind === 'warn',
                    }}
                    role={dashImageMsg().kind === 'error' ? 'alert' : 'status'}
                  >{dashImageMsg().text}</p>
                </Show>
                <div class="v2-home__bg-controls">
                  <label class="v2-home__bg-field">
                    <span>Fit</span>
                    <select
                      class="v2-themed-select v2-home__theme-input"
                      value={dashFit()}
                      disabled={!dashImage()}
                      onChange={(event) => setDashboardImageFit(event.currentTarget.value)}
                      aria-label="Dashboard image fit"
                    >
                      <For each={DASH_IMAGE_FITS}>{(fit) => <option value={fit.id}>{fit.label}</option>}</For>
                    </select>
                  </label>
                  <label class="v2-home__bg-field">
                    <span>Crop X</span>
                    <input
                      type="range"
                      min="0" max="100" step="1"
                      value={dashPosX()}
                      disabled={!dashImage()}
                      onInput={(event) => setDashboardImagePosition('x', event.currentTarget.value)}
                      aria-label="Dashboard image horizontal crop position"
                    />
                    <b>{dashPosX()}%</b>
                  </label>
                  <label class="v2-home__bg-field">
                    <span>Crop Y</span>
                    <input
                      type="range"
                      min="0" max="100" step="1"
                      value={dashPosY()}
                      disabled={!dashImage()}
                      onInput={(event) => setDashboardImagePosition('y', event.currentTarget.value)}
                      aria-label="Dashboard image vertical crop position"
                    />
                    <b>{dashPosY()}%</b>
                  </label>
                </div>
                <p class="v2-home__hint v2-home__hint--small">Allowed: {DASH_IMAGE_ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(', ')}. Uploads stay in this browser, capped at {formatBytes(DASH_IMAGE_UPLOAD_MAX_BYTES)} source / {formatBytes(DASH_IMAGE_LOCAL_STORAGE_MAX_CHARS)} encoded; remote URLs are capped at {formatBytes(DASH_IMAGE_REMOTE_MAX_BYTES)} when size headers are available.</p>
              </div>
              <div class="v2-home__display">
                <p class="v2-home__hint">Display tuning — keep the neon stack readable without going full eye-bleach.</p>

                <div class="v2-home__display-row">
                  <span class="v2-home__display-label">CRT glow</span>
                  <input
                    type="range"
                    class="v2-home__glow-slider"
                    min="0" max="100" step="1"
                    value={glow()}
                    style={{ '--pct': `${glow()}%` }}
                    onInput={(e) => pickGlow(+e.currentTarget.value)}
                    aria-label="CRT glow intensity (percent)"
                  />
                  <span class="v2-home__display-val">{glow()}%</span>
                </div>

                <div class="v2-home__display-row">
                  <span class="v2-home__display-label">Brightness</span>
                  <input
                    type="range"
                    class="v2-home__glow-slider"
                    min="0" max="100" step="1"
                    value={neon()}
                    style={{ '--pct': `${neon()}%` }}
                    onInput={(e) => pickNeon(+e.currentTarget.value)}
                    aria-label="Neon text brightness (percent)"
                  />
                  <span class="v2-home__display-val">{neon()}%</span>
                </div>

                <div class="v2-home__display-row">
                  <span class="v2-home__display-label">Text size</span>
                  <input
                    type="range"
                    class="v2-home__glow-slider"
                    min="85" max="130" step="1"
                    value={fontScale()}
                    style={{ '--pct': `${((fontScale() - 85) / 45) * 100}%` }}
                    onInput={(e) => pickFontScale(+e.currentTarget.value)}
                    aria-label="Global v2 text size (percent)"
                  />
                  <span class="v2-home__display-val">{fontScale()}%</span>
                </div>

                <div class="v2-home__display-row">
                  <span class="v2-home__display-label">Border</span>
                  <input
                    type="range"
                    class="v2-home__glow-slider"
                    min="1" max="3" step="1"
                    value={wmBorder()}
                    style={{ '--pct': `${((wmBorder() - 1) / 2) * 100}%` }}
                    onInput={(e) => pickBorder(+e.currentTarget.value)}
                    aria-label="Window border thickness (pixels)"
                  />
                  <span class="v2-home__display-val">{wmBorder()}px</span>
                </div>
              </div>
            </div>
            <Show when={customEditorOpen()}>
              <Portal>{renderCustomThemeEditor()}</Portal>
            </Show>
          </Show>

          {/* ─── Settings ──────────────────────────────────────────── */}
          <Show when={tab() === 'settings'}>
            <div class="v2-home__pane v2-home__settings">
              <p class="v2-home__hint">Hover hints — show a quick tip about anything under the cursor.</p>
              <label class="v2-home__row">
                <span class="v2-home__settings-label">Hint intensity</span>
                <span class="v2-home__hint-intensity" role="group" aria-label="Hint intensity">
                  {HINT_LEVELS.map((lvl) => (
                    <button
                      type="button"
                      class="v2-btn-sm"
                      classList={{ 'is-active': hints.intensity() === lvl }}
                      data-hint={lvl === 'off'
                        ? 'Disable all hover hints.'
                        : lvl === 'low'
                          ? 'Show only critical hints (e.g. destructive actions).'
                          : 'Show every hint, everywhere.'}
                      data-hint-level="critical"
                      onClick={() => hints.setIntensity(lvl)}
                    >{lvl[0].toUpperCase() + lvl.slice(1)}</button>
                  ))}
                </span>
              </label>
              <label class="v2-home__row"
                data-hint={'How long the cursor must rest before a hint appears.\nLower = snappier, higher = less intrusive on incidental hover.'}
                data-hint-level="critical">
                <span class="v2-home__settings-label">Hint delay</span>
                <input
                  type="range"
                  min={HINT_DELAY_MIN}
                  max={HINT_DELAY_MAX}
                  step="25"
                  value={hints.delayMs()}
                  onInput={(e) => hints.setDelayMs(e.currentTarget.value)}
                  class="v2-home__settings-slider"
                  aria-label="Hint hover-intent delay in milliseconds"
                />
                <span class="v2-home__display-val">{hints.delayMs()}ms</span>
              </label>
              <Show when={wm.state.viewer} fallback={<p class="v2-home__hint v2-home__hint--small">Log in for more user preferences.</p>}>
                {/* #336 — Taskbar always-visible toggle. Stored in localStorage,
                    available to all users (anon + logged-in).
                    Placed inside this Show block so it has visual separation via
                    the existing section header. */}
                <p class="v2-home__hint">Per-user UI preferences. Saved to your account.</p>
                <p class="v2-home__hint" style={{ 'margin-top': 'var(--sp-3)' }}>Taskbar</p>
                <label class="v2-home__row">
                  <input
                    type="checkbox"
                    checked={taskbarStore.alwaysVisible()}
                    onChange={(e) => taskbarStore.setAlwaysVisible(e.currentTarget.checked)}
                  />
                  <span>Always show taskbar</span>
                  <CrtTooltip text={'When on, the taskbar is always visible at the bottom with a Start button.\nWhen off, it only appears when windows are minimised and the hamburger button appears top-right.'}>
                    <span class="v2-home__tip-icon" tabIndex="0" aria-label="About always-visible taskbar">?</span>
                  </CrtTooltip>
                </label>
                <label class="v2-home__row">
                  <input
                    type="checkbox"
                    checked={compact()}
                    onChange={(e) => savePrefPatch({ compactWindows: e.currentTarget.checked })}
                  />
                  <span>Compact window chrome</span>
                  <CrtTooltip text="Reduces window title bar height and button padding — keeps more vertical space for widget content. Good for information-dense dashboards.">
                    <span class="v2-home__tip-icon" tabIndex="0" aria-label="What is compact window chrome?">?</span>
                  </CrtTooltip>
                </label>
                <label class="v2-home__row">
                  <input
                    type="checkbox"
                    checked={!!(prefs()?.muteToasts)}
                    onChange={(e) => savePrefPatch({ muteToasts: e.currentTarget.checked })}
                  />
                  <span>Mute non-error toasts</span>
                </label>
                <label class="v2-home__row">
                  <input
                    type="checkbox"
                    checked={prefs()?.confirmCloseWindows !== false}
                    onChange={(e) => savePrefPatch({ confirmCloseWindows: e.currentTarget.checked })}
                  />
                  <span>Confirm before closing windows</span>
                </label>
                <label class="v2-home__row"
                  data-hint={'Hide your Discord avatar / ID from other players in the public v2 UI.\n(Linking already implies consent — toggle off if you change your mind.)'}
                  data-hint-level="critical">
                  <input
                    type="checkbox"
                    checked={hideAvatar()}
                    onChange={(e) => toggleHideAvatar(e.currentTarget.checked)}
                  />
                  <span>Hide my Discord avatar/ID from public view</span>
                </label>
                <Show when={isAdmin()}>
                  <hr />
                  <p class="v2-home__hint">Admin — Data scope</p>
                  <label class="v2-home__row"
                    data-hint={'When on, sales and guild widgets show data from ALL tracked guilds, not just the three Redfur guilds.\nSession-scoped: resets on logout.'}
                    data-hint-level="critical">
                    <input
                      type="checkbox"
                      checked={adminViewAllGuilds()}
                      onChange={(e) => toggleAdminViewAll(e.currentTarget.checked)}
                    />
                    <span>Show all tracked guilds (not just Redfur)</span>
                  </label>
                </Show>
                <p class="v2-home__hint v2-home__hint--small">
                  Per-widget data-source pickers live in each widget's
                  ⚙ settings panel (not here).
                </p>
              </Show>
              {/* Issue #267 — Account settings (Profile, Privacy, API
                  Keys, Memories) used to live in a floating Settings
                  widget. Migrated here as collapsible sections so there
                  is a single settings surface. */}
              <SettingsHome
                active={() => tab() === 'settings'}
                loggedIn={() => !!wm.state.viewer}
              />
            </div>
          </Show>

          {/* ─── Account ───────────────────────────────────────────── */}
          <Show when={tab() === 'account'}>
            <div class="v2-home__pane v2-home__account">
              <Show
                when={!isAnon()}
                fallback={
                  <div class="v2-home__account-anon">
                    <p>You are browsing anonymously.</p>
                    <a href="/auth/login" class="v2-home__btn v2-home__btn--primary">Login with Discord</a>
                    {renderFamilyDeviceSection()}
                    {renderTestingGroundSection()}
                  </div>
                }
              >
                <div class="v2-home__account-id">
                  <div class="v2-home__account-avatar"
                       classList={{ 'v2-home__account-avatar--img': !!(authMe()?.avatar || wm.state.viewer?.avatar) }}>
                    <Show
                      when={authMe()?.avatar || wm.state.viewer?.avatar}
                      fallback={(authMe()?.displayName || '?').slice(0, 1).toUpperCase()}
                    >
                      <img
                        src={withAvatarSize(authMe()?.avatar || wm.state.viewer?.avatar, 64)}
                        alt={(authMe()?.displayName || 'avatar') + ' avatar'}
                        onError={(e) => { e.currentTarget.parentElement.classList.remove('v2-home__account-avatar--img'); e.currentTarget.replaceWith(document.createTextNode((authMe()?.displayName || '?').slice(0, 1).toUpperCase())); }}
                      />
                    </Show>
                  </div>
                  <div class="v2-home__account-name">
                    <strong>{authMe()?.displayName || wm.state.viewer?.id || 'User'}</strong>
                    <Show when={isAdmin()}>
                      <span class="v2-home__badge v2-home__badge--admin">ADMIN</span>
                    </Show>
                  </div>
                </div>

                <div class="v2-home__account-row">
                  <span class="v2-home__account-label">Discord</span>
                  <span class="v2-home__account-val">
                    <span class="v2-home__dot v2-home__dot--ok" />
                    {authMe()?.username || authMe()?.displayName || 'Linked'}
                  </span>
                </div>

                <div class="v2-home__account-row">
                  <span class="v2-home__account-label">ESO @-tag</span>
                  <span class="v2-home__account-val">
                    <Show
                      when={authMe()?.atTag || wm.state.identity?.tag}
                      fallback={<><span class="v2-home__dot v2-home__dot--warn" />Not set</>}
                    >
                      <span class="v2-home__dot v2-home__dot--ok" />
                      @{String(authMe()?.atTag || wm.state.identity?.tag || '').replace(/^@+/, '')}
                    </Show>
                  </span>
                </div>

                <div class="v2-home__account-row">
                  <span class="v2-home__account-label">Fissal Relay</span>
                  <span class="v2-home__account-val">
                    <Show
                      when={authMe()?.relayLinked}
                      fallback={<><span class="v2-home__dot v2-home__dot--warn" />Not linked</>}
                    >
                      <span class="v2-home__dot v2-home__dot--ok" />
                      {authMe()?.relayDisplayName && authMe()?.relayDisplayName !== 'Unknown'
                        ? `Paired: ${authMe().relayDisplayName}`
                        : 'Device paired'}
                    </Show>
                  </span>
                </div>

                <button class="v2-home__btn v2-home__btn--primary" onClick={() => setRelaySetupOpen(true)}>Set up Fissal Relay</button>

                <div class="v2-home__account-actions">
                  <button class="v2-home__btn" onClick={() => refetchMe()}>↻ Refresh</button>
                  <button class="v2-home__btn v2-home__btn--danger" onClick={logout}>🚪 Logout</button>
                </div>
                {renderFamilyDeviceSection()}
                {renderTestingGroundSection()}
              </Show>
            </div>
          </Show>

          {/* ─── Layout ────────────────────────────────────────────── */}
          <Show when={tab() === 'layout'}>
            <div class="v2-home__pane v2-home__layout">
              <p class="v2-home__hint">Bulk operations on the active dashboard.</p>
              <button class="v2-home__btn" onClick={doRestoreAll}>↺ Restore all minimized / maximized windows</button>
              <button class="v2-home__btn" onClick={() => { wm.arrangeTile(); closePanel(); }}>▦ Tile</button>
              <button class="v2-home__btn" onClick={() => { wm.arrangeCascade(); closePanel(); }}>⌥ Cascade</button>
              <button class="v2-home__btn" onClick={() => { wm.arrangeDesktopIcons(); closePanel(); }}>📌 Tidy desktop shortcuts</button>
              <button class="v2-home__btn v2-home__btn--danger" onClick={(e) => onResetClick(e)}>⚠ Reset to defaults</button>
              <Show when={isAdmin()}>
                <hr />
                <p class="v2-home__hint">Admin</p>
                <button class="v2-home__btn" onClick={saveSpyTemplate}>💾 Save current layout as Spy template</button>
              </Show>
              <hr />
              <p class="v2-home__hint">Switch interface</p>
              <a class="v2-home__btn" href="/legacy/" style={{ display: 'inline-block', 'text-decoration': 'none' }}>← Advanced (legacy v1) dashboard</a>
            </div>
          </Show>

          {/* #344 — near-cursor confirm for the destructive reset action. */}
          <Show when={resetConfirm()}>
            <Portal>
              <div
                class="v2-reset-confirm"
                style={{
                  position: 'fixed',
                  left: `${Math.min(resetConfirm().x, window.innerWidth - 220)}px`,
                  top: `${Math.min(resetConfirm().y + 8, window.innerHeight - 80)}px`,
                  'z-index': 'var(--z-overlay)',
                }}
                onMouseLeave={clearResetConfirm}
                role="dialog"
                aria-label="Confirm reset layout"
              >
                <span class="v2-reset-confirm__text">Reset layout to defaults?</span>
                <div class="v2-reset-confirm__btns">
                  <button type="button" class="v2-btn-sm v2-home__btn--danger" onClick={confirmReset}>Reset</button>
                  <button type="button" class="v2-btn-sm" onClick={clearResetConfirm}>Cancel</button>
                </div>
              </div>
            </Portal>
          </Show>

          {/* ─── Dashboards / Desks (#2, #338) ─────────────────────── */}
          <Show when={tab() === 'dashboards'}>
            <div class="v2-home__pane v2-home__dashboards">
              <p class="v2-home__hint">Switch between desktops. Each keeps its own widgets, sizes, and positions.<Show when={wm.state.viewer && wm.state.dashboards.filter(d => d.id !== 'anon' && !d._spyTab).length > 1}> Drag a desktop to reorder.</Show></p>
              <ul class="v2-home__dash-list">
                <For each={wm.state.dashboards}>{(d) => (
                  <li
                    class="v2-home__dash-item"
                    classList={{
                      'is-active': d.id === wm.state.activeDashboardId,
                      'is-dragging': dragDashId() === d.id,
                      'is-drop-target': overDashId() === d.id && !!dragDashId() && dragDashId() !== d.id,
                    }}
                    draggable={canReorderDash(d)}
                    onClick={() => { wm.setActiveDashboard(d.id); closePanel(); }}
                    onDragStart={(e) => onDashDragStart(e, d)}
                    onDragOver={(e) => onDashDragOver(e, d)}
                    onDragLeave={() => { if (overDashId() === d.id) setOverDashId(null); }}
                    onDrop={(e) => onDashDrop(e, d)}
                    onDragEnd={onDashDragEnd}
                  >
                    <span class="v2-home__dash-emoji">{d.emoji || EMOJI.window}</span>
                    <span class="v2-home__dash-name">{d.name}</span>
                    <Show when={d.id !== 'anon' && wm.state.viewer && !d._spyTab}>
                      <button
                        class="v2-home__dash-del"
                        title={`Rename “${d.name}”`}
                        aria-label="Rename desktop"
                        onClick={(e) => { e.stopPropagation(); renameDash(d); }}
                      >✎</button>
                    </Show>
                    <Show when={d.id !== 'anon' && wm.state.viewer && !d._spyTab && wm.state.dashboards.length > 1}>
                      <button
                        class="v2-home__dash-del"
                        title={`Delete “${d.name}”`}
                        aria-label="Delete desktop"
                        onClick={(e) => { e.stopPropagation(); deleteDash(d); }}
                      >✖</button>
                    </Show>
                  </li>
                )}</For>
              </ul>
              <Show when={wm.state.viewer}>
                <button class="v2-home__btn" onClick={addDash}>+ New desktop</button>
              </Show>
              <Show when={isAnon()}>
                <p class="v2-home__hint v2-home__hint--small">Log in to save and switch between multiple desktops.</p>
              </Show>
            </div>
          </Show>
        </div>
      </Show>
      <Show when={relaySetupOpen()}>
        <RelaySetupDialog open={relaySetupOpen()} onClose={() => setRelaySetupOpen(false)} onChanged={() => refetchMe()} />
      </Show>
    </>
  );
}
